import { TextareaHTMLAttributes, forwardRef } from 'react'
import { motion } from 'framer-motion'

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
  showCount?: boolean
  maxLength?: number
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      helperText,
      showCount = false,
      maxLength,
      className = '',
      value,
      ...props
    },
    ref
  ) => {
    const textLength = typeof value === 'string' ? value.length : 0
    
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
            {label}
          </label>
        )}
        <motion.div
          whileFocus={{ scale: 1.005 }}
          transition={{ duration: 0.15 }}
        >
          <textarea
            ref={ref}
            value={value}
            maxLength={maxLength}
            className={`
              w-full px-4 py-3 rounded-lg
              bg-dark-bg-secondary border border-dark-border
              text-dark-text-primary placeholder-dark-text-muted
              focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500
              transition-all duration-200
              resize-y min-h-[120px]
              ${error ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
              ${className}
            `}
            {...props}
          />
        </motion.div>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex-1">
            {error && (
              <p className="text-sm text-red-400">{error}</p>
            )}
            {!error && helperText && (
              <p className="text-sm text-dark-text-muted">{helperText}</p>
            )}
          </div>
          {showCount && (
            <span className="text-xs text-dark-text-muted">
              {textLength}{maxLength && `/${maxLength}`}
            </span>
          )}
        </div>
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'
